import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail, MapPin, Phone } from "lucide-react";
import { useEffect, useState } from "react";

import { findCollectionItem } from "@/lib/collection";
import { fetchPublishedStudioPieces } from "@/lib/studio-public.functions";
import { CONTACT_STEPS, CUSTOM_INTEREST, DEFAULT_INTEREST, GENERAL_FAQ } from "@/lib/content";
import { EMAIL, PHONE_DISPLAY, PHONE_E164 } from "@/lib/site";
import { jsonLdScript, pageHead, PAGE_SEO, webPageGraph } from "@/lib/seo";

import { Reveal } from "@/components/site/reveal";
import { Eyebrow } from "@/components/site/eyebrow";
import { ContactForm } from "@/components/site/contact-form";
import { faqJsonLd, FaqList } from "@/components/site/faq-list";

type ContactSearch = {
  piece?: string;
  interest?: string;
};

export const Route = createFileRoute("/contact")({
  validateSearch: (search: Record<string, unknown>): ContactSearch => {
    const next: ContactSearch = {};
    const piece = search["piece"];
    const interest = search["interest"];
    if (typeof piece === "string") next.piece = piece;
    if (typeof interest === "string") next.interest = interest;
    return next;
  },
  component: ContactPage,
  head: () => {
    const graph = webPageGraph(PAGE_SEO.contact);
    return {
      ...pageHead({
        ...PAGE_SEO.contact,
        scripts: [
          jsonLdScript({
            ...graph,
            "@graph": [...graph["@graph"], faqJsonLd(GENERAL_FAQ)],
          }),
        ],
      }),
    };
  },
});

function ContactPage() {
  const { piece, interest } = Route.useSearch();
  const builtIn = findCollectionItem(piece);
  const [studioName, setStudioName] = useState<string | undefined>();

  useEffect(() => {
    if (!piece || builtIn) return;
    let cancelled = false;
    fetchPublishedStudioPieces().then((items) => {
      if (cancelled) return;
      setStudioName(items.find((item) => item.slug === piece)?.name);
    });
    return () => {
      cancelled = true;
    };
  }, [piece, builtIn]);

  const itemName = builtIn?.name ?? studioName;
  const defaultInterest = interest === "custom" ? CUSTOM_INTEREST : DEFAULT_INTEREST;
  const defaultMessage = itemName ? `Hi Ella — I'm interested in the ${itemName}.` : "";

  return (
    <main id="main" tabIndex={-1}>
      <section className="pb-16 pt-16 sm:pb-24 sm:pt-24 lg:pb-32 lg:pt-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>SIMPLE &amp; PERSONAL</Eyebrow>
            <h1 className="mt-5 text-4xl leading-[1.08] sm:text-5xl lg:text-6xl">
              From hello to{" "}
              <em className="accent-underline font-display italic text-primary">happy.</em>
            </h1>
          </Reveal>

          <ol className="mt-14 grid gap-7 md:grid-cols-3">
            {CONTACT_STEPS.map((s, i) => (
              <Reveal as="li" key={s.title} delay={i * 100}>
                <div className="h-full rounded-3xl bg-secondary p-8">
                  <span className="font-display text-4xl text-primary/40">0{i + 1}</span>
                  <div className="mt-5 stitch-rule text-primary" aria-hidden="true" />
                  <h3 className="mt-5 font-display text-xl">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{s.copy}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-plum-deep py-16 text-plum-foreground sm:py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-[0.9fr_1fr] lg:px-10">
          <Reveal>
            <Eyebrow>GET IN TOUCH</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Ready to find something <em className="font-display italic text-gold">special?</em>
            </h2>
            <p className="mt-6 max-w-md leading-relaxed text-plum-foreground/75">
              Ask about a specific item, sizing for 18-inch dolls, or a custom order for a gift.
              Ella answers personally and is always happy to help you choose.
            </p>

            <address className="mt-10 not-italic">
              <ul className="space-y-4 text-sm">
                <li>
                  <a
                    href={`tel:${PHONE_E164}`}
                    className="inline-flex min-h-11 items-center gap-3 transition-colors hover:text-gold"
                  >
                    <Phone className="size-4 text-gold" aria-hidden="true" /> Call {PHONE_DISPLAY}
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${EMAIL}`}
                    className="inline-flex min-h-11 items-center gap-3 transition-colors hover:text-gold"
                  >
                    <Mail className="size-4 text-gold" aria-hidden="true" /> {EMAIL}
                  </a>
                </li>
                <li className="inline-flex min-h-11 items-center gap-3">
                  <MapPin className="size-4 text-gold" aria-hidden="true" /> Midland, Michigan
                </li>
              </ul>
            </address>
          </Reveal>

          <Reveal delay={120}>
            <div className="rounded-[2rem] bg-card p-7 text-card-foreground shadow-[var(--shadow-lift)] sm:p-9">
              <ContactForm
                key={`${piece ?? ""}-${interest ?? ""}-${itemName ?? ""}`}
                defaultInterest={defaultInterest}
                defaultMessage={defaultMessage}
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[0.9fr_1fr] lg:px-10">
          <Reveal>
            <Eyebrow>WHAT HAPPENS AFTER YOU REACH OUT</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              A real person,{" "}
              <em className="accent-underline font-display italic text-primary">every time.</em>
            </h2>
          </Reveal>
          <Reveal delay={120} className="space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>
              Ella reads and answers every message herself — there's no shop assistant and no
              automated reply. She'll get back to you personally about the piece or idea you
              mentioned.
            </p>
            <p>
              Then she asks a few friendly questions: who the gift is for, what colors they love,
              how they play, and whether there's a date to work toward. That's usually all it takes
              for her to suggest something.
            </p>
            <p>
              From there she'll share options — a piece from{" "}
              <Link to="/collection" className="text-gold-ink underline underline-offset-4">
                the collection
              </Link>
              , a matching accessory, or a{" "}
              <Link to="/personalization" className="text-gold-ink underline underline-offset-4">
                made-to-order version
              </Link>{" "}
              — and confirm pricing and timing before anything is made. No pressure, and no
              obligation to buy.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-secondary py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>FREQUENTLY ASKED</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Questions people{" "}
              <em className="accent-underline font-display italic text-primary">ask Ella.</em>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Don't see your question here? Call {PHONE_DISPLAY}, email {EMAIL}, or send the form
              above — Ella is always happy to help.
            </p>
          </Reveal>

          <FaqList items={GENERAL_FAQ} />
        </div>
      </section>
    </main>
  );
}
